/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable indent */
import i18n from 'i18n';
import { messagesRequest } from 'modules/messages/actions';
import { push } from 'react-router-redux';
import { all, put, takeLatest } from 'redux-saga/effects';
import { isRequestOK, parseJwt } from 'utils/helpers';
import {
  authenticateFulfilled,
  authenticateMFAFulfilled,
  authenticateMFARejected,
  authenticateMFARequest,
  authenticateRejected,
  authenticateRequest,
  forgotPasswordRequest,
  forgotPasswordRequestFulfilled,
  forgotPasswordRequestRejected,
  mfaChangeRegionFulfilled,
  registerPasswordFulfilled,
  registerPasswordRejected,
  registerPasswordRequest,
  authCheckRestrictionsFulfilled,
  authCheckRestrictionsRejected,
  authCheckRestrictionsRequest,
  trustedLoginRequest,
  trustedLoginFulfilled,
  trustedLoginRejected,
  authenticateLogout,
} from './actions';
import endpoints, { api } from './endpoints';
import { AuthenticateResponse, DecodedTokenPayload } from './interfaces';
import {
  AuthenticateMFARequestPayload,
  AuthenticateRequestPayload,
  ForgotPasswordRequestProps,
  RegisterPasswordRequestProps,
} from './sagaInterfaces';
import { validateTrustedToken } from './utils';

const LOGGED_REGION_KEY = 'logged_region';

export function* authenticateRequestSaga({
  payload,
}: AuthenticateRequestPayload) {
  const { callback, ...props } = payload;

  try {
    const { status, data }: AuthenticateResponse = yield api.post({
      url: endpoints.login(),
      data: props,
    });

    // eslint-disable-next-line no-unused-expressions
    callback && callback(data);

    if (data?.available_regions) {
      const region = data?.available_regions.find(
        region => region.code === data?.logged_region
      );
      localStorage.setItem(LOGGED_REGION_KEY, JSON.stringify(region));
    }

    if (payload.isChangeRegion) {
      return yield all([put(mfaChangeRegionFulfilled(data))]);
    }

    if (!isRequestOK(status)) {
      return yield all([put(authenticateRejected())]);
    }

    if (data?.mfa?.required) {
      return yield all([
        put(authenticateFulfilled(data)),
        put(push('/authenticate/mfa')),
      ]);
    }

    const decodedToken: DecodedTokenPayload | null =
      data?.token && parseJwt(data.token);

    if (!decodedToken || decodedToken.mfa_required !== false) {
      return yield all([
        put(authenticateRejected()),
        put(
          messagesRequest({
            type: 'error',
            message: i18n.t('UNEXPECTED_ERROR'),
          })
        ),
      ]);
    }

    return yield all([put(authenticateFulfilled(data))]);
  } catch (error) {
    return yield put(authenticateRejected());
  }
}

function* authenticateLogoutSaga() {
  try {
    yield api.post({
      url: endpoints.logout(),
    });
  } catch (_) {
    //
  } finally {
    localStorage.removeItem(LOGGED_REGION_KEY);
    yield put({ type: 'CLEAR_STORE' });
    yield put(push('/'));
  }
}

function* authCheckRestrictionsRequestSaga() {
  try {
    const { data } = yield api.get({
      url: endpoints.checkRestrictions(),
    });

    return yield put(authCheckRestrictionsFulfilled(data));
  } catch (e) {
    return yield put(authCheckRestrictionsRejected());
  }
}

export function* authenticateMFARequestSaga({
  payload,
}: AuthenticateMFARequestPayload) {
  const { callback, ...props } = payload;

  try {
    const { status, data }: AuthenticateResponse = yield api.post({
      url: endpoints.mfa(),
      data: props,
    });

    // eslint-disable-next-line no-unused-expressions
    callback && callback(data);

    if (!isRequestOK(status)) {
      return yield all([put(authenticateMFARejected())]);
    }

    const decodedToken: DecodedTokenPayload | null =
      data?.token && parseJwt(data.token);

    if (!decodedToken || decodedToken.mfa_verified !== true) {
      return yield all([
        put(authenticateMFARejected()),
        put(
          messagesRequest({
            type: 'error',
            message: i18n.t('UNEXPECTED_ERROR'),
          })
        ),
      ]);
    }

    return yield all([put(authenticateMFAFulfilled(data)), put(push('/'))]);
  } catch (error) {
    return yield put(authenticateMFARejected());
  }
}

function* forgotPasswordRequestSaga({ payload }: ForgotPasswordRequestProps) {
  try {
    const { status } = yield api.post({
      url: endpoints.resetPassword(),
      data: payload,
    });

    return yield isRequestOK(status)
      ? all([
          put(forgotPasswordRequestFulfilled()),
          put(
            messagesRequest({
              type: 'success',
              message: i18n.t('EMAIL_RECOVER_KEY'),
            })
          ),
        ])
      : all([put(forgotPasswordRequestRejected())]);
  } catch (responseWithError) {
    return yield responseWithError &&
    responseWithError.response &&
    responseWithError.response.status === 452
      ? put(forgotPasswordRequestRejected())
      : all([put(forgotPasswordRequestRejected())]);
  }
}

function* registerPasswordRequestSaga({
  payload,
}: RegisterPasswordRequestProps) {
  try {
    const { status } = yield api.post({
      url: endpoints.update(),
      data: payload,
    });

    return yield isRequestOK(status)
      ? all([
          put(registerPasswordFulfilled(payload)),
          put(
            messagesRequest({
              type: 'success',
              message: i18n.t('PASSWORD_CHANGE_KEY'),
            })
          ),
        ])
      : all([put(registerPasswordRejected())]);
  } catch (responseWithError) {
    return yield put(registerPasswordRejected());
  }
}

function* trustedLoginRequestSaga({ payload }: { payload: string }) {
  try {
    const isTokenValid = validateTrustedToken(payload);

    if (!isTokenValid) {
      return yield all([put(trustedLoginRejected()), put(push('/'))]);
    }

    const { status, data } = yield api.post({
      url: endpoints.trustedLogin(),
      data: { token: payload },
    });

    return yield isRequestOK(status)
      ? all([
          put(trustedLoginFulfilled(payload)),
          put(authenticateFulfilled(data)),
        ])
      : all([put(trustedLoginRejected()), put(push('/'))]);
  } catch (responseWithError) {
    return yield all([put(trustedLoginRejected()), put(push('/'))]);
  }
}

export default [
  takeLatest(authenticateRequest, authenticateRequestSaga),
  takeLatest(forgotPasswordRequest, forgotPasswordRequestSaga),
  takeLatest(authenticateMFARequest, authenticateMFARequestSaga),
  takeLatest(registerPasswordRequest, registerPasswordRequestSaga),
  takeLatest(authCheckRestrictionsRequest, authCheckRestrictionsRequestSaga),
  takeLatest(trustedLoginRequest, trustedLoginRequestSaga),
  takeLatest(authenticateLogout, authenticateLogoutSaga),
];
